import {
  Injectable, inject,
} from '@angular/core'
import { LOGGER_SINKS } from '../tokens/logger.tokens'

export interface LogSink {

  log(
    level: LogLevel,
    message: string,
    ...meta: { [key: string]: unknown }[]
  ): void

}

export enum Loggers {
  Renderer = 'ngx-components.renderer',
}

// See node_modules/triple-beam/config/syslog
export enum LogLevel {
  Debug = 'debug',
  Info = 'info',
  Notice = 'notice',
  Warning = 'warning',
  Error = 'error',
  Crit = 'crit',
  Alert = 'alert',
  Emerg = 'emerg',
}

export class Logger {
  public constructor(
    private sink?: LogSink | null,
  ) {}

  private log(
    level: LogLevel,
    message: string,
    ...meta: { [key: string]: unknown }[]
  ) {
    if (this.sink) {
      this.sink.log(
        level,
        message,
        ...meta,
      )
    } else {
      let msg: string | Error = message
      for (const metaObject of meta) {
        if (metaObject instanceof Object) {
          for (const [metaKey, metaValue] of Object.entries(metaObject)) {
            if (metaValue instanceof Error) {
              msg = new Error(message, { cause: metaValue })
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
              delete metaObject[metaKey]
              break
            }
          }
        }
      }

      if (level === LogLevel.Error || level === LogLevel.Crit || level === LogLevel.Emerg || level === LogLevel.Alert) {
        console.error(msg, ...meta)
      } else if (level === LogLevel.Warning) {
        console.warn(msg, ...meta)
      } else if (level === LogLevel.Info || level === LogLevel.Notice) {
        console.info(msg, ...meta)
      } else {
        console.debug(msg, ...meta)
      }
    }
  }

  public debug(message: string, ...meta: { [key: string]: unknown }[]) {
    this.log(LogLevel.Debug, message, ...meta)
  }

  public info(message: string, ...meta: { [key: string]: unknown }[]) {
    this.log(LogLevel.Info, message, ...meta)
  }

  public notice(message: string, ...meta: { [key: string]: unknown }[]) {
    this.log(LogLevel.Notice, message, ...meta)
  }

  public warning(message: string, ...meta: { [key: string]: unknown }[]) {
    this.log(LogLevel.Warning, message, ...meta)
  }

  public error(message: string, ...meta: { [key: string]: unknown }[]) {
    this.log(LogLevel.Error, message, ...meta)
  }

  public crit(message: string, ...meta: { [key: string]: unknown }[]) {
    this.log(LogLevel.Crit, message, ...meta)
  }

  public alert(message: string, ...meta: { [key: string]: unknown }[]) {
    this.log(LogLevel.Alert, message, ...meta)
  }

  public emerg(message: string, ...meta: { [key: string]: unknown }[]) {
    this.log(LogLevel.Emerg, message, ...meta)
  }
}

export function getLogger(name: string) {
  return inject(LoggerService).getLogger(name)
}

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private loggerSinks?: {
    name: string
    sink: LogSink
  }[]

  public constructor() {
    const sinks = inject(LOGGER_SINKS, { optional: true })
    this.loggerSinks = sinks
      ? Object.entries(sinks).map(([name, sink]) => ({
          name,
          sink,
        }))
      : undefined
  }

  public getLogger(name: string) {
    if (this.loggerSinks) {
      const sink = this.loggerSinks.find(sink => name.startsWith(sink.name))
      return new Logger(sink?.sink)
    }
    return new Logger()
  }
}
